// src/profile/ProfileWithdraw.jsx
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './tabs.css';

const MIN_WITHDRAW_RUB = 5000;

const styles = {
  root: {
    color: '#e5e7eb',
    fontFamily:
      "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",

    // ✅ важно: чтобы компонент мог нормально жить внутри вкладки
    height: '100vh',
    minHeight: 0,
  },

  card: {
    background: 'radial-gradient(circle at top left,#1f2937,#020617)',
    borderRadius: 20,
    padding: 24,
    border: '1px solid rgba(148,163,184,0.35)',
    boxShadow: '0 24px 60px rgba(0,0,0,0.7)',
    maxWidth: 840,

    // ✅ чтобы внутренний скролл работал корректно
    width: '100%',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,

    // ✅ ограничиваем высоту карточки, чтобы “низ” не резался родителем
    maxHeight: 'calc(100vh - 220px)',
    overflow: 'hidden',
  },

  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 24,
    marginBottom: 16,
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 10px',
    borderRadius: 999,
    fontSize: 11,
    textTransform: 'uppercase',
    fontWeight: 600,
    letterSpacing: 0.4,
    background: 'rgba(248,250,252,0.04)',
    border: '1px solid rgba(148,163,184,0.8)',
    color: '#e5e7eb',
    marginBottom: 6,
  },
  title: {
    margin: 0,
    fontSize: 22,
    fontWeight: 700,
  },
  subtitle: {
    marginTop: 8,
    fontSize: 13,
    color: '#9ca3af',
    maxWidth: 460,
    lineHeight: 1.5,
  },

  iconWrap: {
    minWidth: 140,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  bigIconCircle: {
    width: 80,
    height: 80,
    borderRadius: '50%',
    background:
      'radial-gradient(circle at 30% 0,#1f2937 0,#020617 60%,#020617 100%)',
    border: '1px solid rgba(148,163,184,0.7)',
    boxShadow: '0 0 0 4px rgba(15,23,42,0.9), 0 10px 25px rgba(0,0,0,0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  tabs: {
    display: 'flex',
    gap: 8,
    margin: '6px 0 14px',
  },
  tabBtn: (active) => ({
    flex: 1,
    height: 38,
    borderRadius: 999,
    border: '1px solid ' + (active ? '#22c55e' : '#334155'),
    background: active ? 'rgba(34,197,94,0.1)' : 'transparent',
    color: active ? '#bbf7d0' : '#e5e7eb',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  }),
  tabIcon: {
    width: 16,
    height: 16,
  },

  separator: {
    height: 1,
    background:
      'linear-gradient(90deg, transparent, rgba(148,163,184,0.4), transparent)',
    margin: '10px 0 16px',
  },

  // ✅ ВАЖНО: скролл ТОЛЬКО для режима "card"
  formScroll: (enabled) =>
    enabled
      ? {
          // высота области под форму: чтобы можно было скроллить и не резало низ
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          paddingRight: 8, // чтобы текст не прятался под скроллбаром
          // ограничим высоту, чтобы область гарантированно стала скроллящейся
          maxHeight: 'min(60vh, 560px)',
        }
      : {
          // crypto: без внутреннего скролла
        },

  form: { display: 'grid', gap: 12, maxWidth: 520 },
  field: { display: 'grid', gap: 4 },
  label: { fontSize: 13, color: '#cbd5f5' },
  hint: { fontSize: 12, color: '#9ca3af' },
  input: {
    height: 40,
    borderRadius: 10,
    border: '1px solid #374151',
    background: '#020617',
    color: '#e5e7eb',
    padding: '0 12px',
    fontSize: 14,
    outline: 'none',
  },
  textarea: {
    minHeight: 60,
    borderRadius: 10,
    border: '1px solid #374151',
    background: '#020617',
    color: '#e5e7eb',
    padding: '8px 12px',
    fontSize: 14,
    outline: 'none',
    resize: 'vertical',
  },

  minNote: {
    marginTop: 4,
    fontSize: 12,
    color: '#facc15',
  },

  submitRow: {
    marginTop: 4,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap',
  },
  btnPrimary: {
    height: 42,
    padding: '0 24px',
    borderRadius: 999,
    border: 'none',
    background:
      'linear-gradient(135deg,#22c55e 0%,#16a34a 40%,#15803d 100%)',
    color: '#022c22',
    fontSize: 15,
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: '0 10px 25px rgba(34,197,94,0.35)',
  },
  btnDisabled: {
    opacity: 0.6,
    cursor: 'default',
    boxShadow: 'none',
  },

  statusText: { fontSize: 13, color: '#bbf7d0' },
  errorText: { fontSize: 13, color: '#fecaca' },
};

export default function ProfileWithdraw() {
  const { t: translate } = useTranslation();
  const API_ORIGIN = process.env.REACT_APP_API_URL;

  const [mode, setMode] = useState('crypto'); // 'crypto' | 'card'

  // crypto
  const [cryptoAddress, setCryptoAddress] = useState('');
  const [cryptoNetwork, setCryptoNetwork] = useState('');
  const [cryptoAmount, setCryptoAmount] = useState('');

  // card
  const [cardNumber, setCardNumber] = useState('');
  const [bankName, setBankName] = useState('');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [cardAmount, setCardAmount] = useState('');

  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  const currentAmount = useMemo(() => {
    const raw = mode === 'crypto' ? cryptoAmount : cardAmount;
    return Number(raw) || 0;
  }, [mode, cryptoAmount, cardAmount]);

  const isAmountTooLow =
    currentAmount > 0 && currentAmount < MIN_WITHDRAW_RUB;

  const canSubmit = useMemo(() => {
    if (loading) return false;
    if (currentAmount < MIN_WITHDRAW_RUB) return false;

    if (mode === 'crypto') {
      return cryptoAddress.trim().length > 0 && cryptoNetwork.trim().length > 0;
    }

    return (
      cardNumber.trim().length >= 8 &&
      bankName.trim().length > 1 &&
      fullName.trim().length > 1 &&
      phone.trim().length >= 5
    );
  }, [
    loading,
    currentAmount,
    mode,
    cryptoAddress,
    cryptoNetwork,
    cardNumber,
    bankName,
    fullName,
    phone,
  ]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setStatus('');

    const amount = currentAmount;
    if (!Number.isFinite(amount) || amount <= 0) {
      setError(translate('profile.withdraw.invalidAmount'));
      return;
    }
    if (amount < MIN_WITHDRAW_RUB) {
      setError(translate('profile.withdraw.minimumError', { amount: MIN_WITHDRAW_RUB }));
      return;
    }

    const token = localStorage.getItem('token') || '';

    const payload =
      mode === 'crypto'
        ? {
            type: 'crypto',
            amount,
            currency: 'RUB',
            walletAddress: cryptoAddress.trim(),
            network: cryptoNetwork.trim(),
          }
        : {
            type: 'card',
            amount,
            currency: 'RUB',
            cardNumber: cardNumber.trim(),
            bankName: bankName.trim(),
            fullName: fullName.trim(),
            phone: phone.trim(),
          };

    payload.source = 'profile-withdraw';

    try {
      setLoading(true);

      const res = await fetch(`${API_ORIGIN}/api/withdrawals/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        let data = null;
        try {
          data = await res.json();
        } catch (e) {
          data = null;
        }

        console.log('withdraw error response:', res.status, data);

        throw new Error(
          (data && (data.error || data.message)) || translate('profile.withdraw.serverError', { status: res.status })
        );
      }

      setStatus(
        translate('profile.withdraw.sent')
      );

      if (mode === 'crypto') {
        setCryptoAddress('');
        setCryptoNetwork('');
        setCryptoAmount('');
      } else {
        setCardNumber('');
        setBankName('');
        setFullName('');
        setPhone('');
        setCardAmount('');
      }
    } catch (e) {
      console.error(e);
      setError(e.message || translate('profile.withdraw.sendError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.root}>
      <div style={styles.card}>
        <div style={styles.headerRow}>
          <div>
            <div style={styles.badge}>{translate('profile.withdraw.badge')}</div>
            <h2 style={styles.title}>{translate('profile.withdraw.title')}</h2>
            <p style={styles.subtitle}>
              {translate('profile.withdraw.subtitle', {
                amount: MIN_WITHDRAW_RUB,
              })}
            </p>
          </div>

          <div style={styles.iconWrap}>
            <div style={styles.bigIconCircle}>
              <svg viewBox="0 0 24 24" width="32" height="32" aria-hidden="true">
                <path
                  d="M4 7h16v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7z"
                  fill="none"
                  stroke="#facc15"
                  strokeWidth="1.7"
                />
                <path
                  d="M4 7V6a2 2 0 0 1 2-2h5"
                  fill="none"
                  stroke="#facc15"
                  strokeWidth="1.7"
                />
                <path d="M13 5h3" fill="none" stroke="#facc15" strokeWidth="1.7" />
                <path
                  d="M12 10v6m-2-4h4"
                  fill="none"
                  stroke="#facc15"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* выбор способа вывода */}
        <div style={styles.tabs}>
          <button
            type="button"
            style={styles.tabBtn(mode === 'crypto')}
            onClick={() => setMode('crypto')}
          >
            <svg viewBox="0 0 24 24" style={styles.tabIcon}>
              <circle
                cx="12"
                cy="12"
                r="8"
                fill="none"
                stroke="#22c55e"
                strokeWidth="1.6"
              />
              <path
                d="M9 12h6M12 9v6"
                fill="none"
                stroke="#22c55e"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
            {translate('profile.withdraw.cryptoWallet')}
          </button>

          <button
            type="button"
            style={styles.tabBtn(mode === 'card')}
            onClick={() => setMode('card')}
          >
            <svg viewBox="0 0 24 24" style={styles.tabIcon}>
              <rect
                x="3"
                y="5"
                width="18"
                height="14"
                rx="2"
                ry="2"
                fill="none"
                stroke="#22c55e"
                strokeWidth="1.6"
              />
              <rect x="3" y="8" width="18" height="3" fill="#22c55e" />
            </svg>
            {translate('profile.withdraw.bankCard')}
          </button>
        </div>

        <div style={styles.separator} />

        {/* ✅ СКРОЛЛ ТОЛЬКО ДЛЯ mode === 'card' */}
        <div style={styles.formScroll(mode === 'card')}>
          <form style={styles.form} onSubmit={handleSubmit}>
            {mode === 'crypto' ? (
              <>
                <div style={styles.field}>
                  <label style={styles.label}>{translate('profile.withdraw.walletAddress')}</label>
                  <input
                    style={styles.input}
                    type="text"
                    value={cryptoAddress}
                    onChange={(e) => setCryptoAddress(e.target.value)}
                    placeholder={translate('profile.withdraw.walletPlaceholder')}
                  />
                  <div style={styles.hint}>
                    {translate('profile.withdraw.walletHint')}
                  </div>
                </div>

                <div style={styles.field}>
                  <label style={styles.label}>{translate('profile.withdraw.network')}</label>
                  <input
                    style={styles.input}
                    type="text"
                    value={cryptoNetwork}
                    onChange={(e) => setCryptoNetwork(e.target.value)}
                    placeholder={translate('profile.withdraw.networkPlaceholder')}
                  />
                  <div style={styles.hint}>
                    {translate('profile.withdraw.networkHint')}
                  </div>
                </div>

                <div style={styles.field}>
                  <label style={styles.label}>{translate('profile.withdraw.amount')}</label>
                  <input
                    style={styles.input}
                    type="number"
                    min={MIN_WITHDRAW_RUB}
                    value={cryptoAmount}
                    onChange={(e) => setCryptoAmount(e.target.value)}
                    placeholder={`${MIN_WITHDRAW_RUB}`}
                  />
                  <div style={styles.hint}>
                    {translate('profile.withdraw.cryptoAmountHint')}
                  </div>
                  {isAmountTooLow && (
                    <div style={styles.minNote}>
                      {translate('profile.withdraw.minimumNote', { amount: MIN_WITHDRAW_RUB })}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <div style={styles.field}>
                  <label style={styles.label}>{translate('profile.withdraw.cardNumber')}</label>
                  <input
                    style={styles.input}
                    type="text"
                    value={cardNumber}
                    onChange={(e) => setCardNumber(e.target.value)}
                    placeholder="0000 0000 0000 0000"
                  />
                  <div style={styles.hint}>
                    {translate('profile.withdraw.cardHint')}
                  </div>
                </div>

                <div style={styles.field}>
                  <label style={styles.label}>{translate('profile.withdraw.bank')}</label>
                  <input
                    style={styles.input}
                    type="text"
                    value={bankName}
                    onChange={(e) => setBankName(e.target.value)}
                    placeholder={translate('profile.withdraw.bankPlaceholder')}
                  />
                </div>

                <div style={styles.field}>
                  <label style={styles.label}>{translate('profile.withdraw.recipientName')}</label>
                  <input
                    style={styles.input}
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder={translate('profile.withdraw.recipientPlaceholder')}
                  />
                </div>

                <div style={styles.field}>
                  <label style={styles.label}>{translate('profile.withdraw.recipientPhone')}</label>
                  <input
                    style={styles.input}
                    type="text"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+7..."
                  />
                </div>

                <div style={styles.field}>
                  <label style={styles.label}>{translate('profile.withdraw.amount')}</label>
                  <input
                    style={styles.input}
                    type="number"
                    min={MIN_WITHDRAW_RUB}
                    value={cardAmount}
                    onChange={(e) => setCardAmount(e.target.value)}
                    placeholder={`${MIN_WITHDRAW_RUB}`}
                  />
                  <div style={styles.hint}>
                    {translate('profile.withdraw.cardAmountHint')}
                  </div>
                  {isAmountTooLow && (
                    <div style={styles.minNote}>
                      {translate('profile.withdraw.minimumNote', { amount: MIN_WITHDRAW_RUB })}
                    </div>
                  )}
                </div>
              </>
            )}

            {/* ✅ делаем кнопку “липкой”, чтобы она была видна даже при скролле */}
            <div
              style={{
                ...styles.submitRow,
                ...(mode === 'card'
                  ? {
                      position: 'sticky',
                      bottom: 0,
                      paddingTop: 10,
                      paddingBottom: 6,
                      background: 'rgba(2,6,23,0.92)',
                      borderTop: '1px solid rgba(148,163,184,0.18)',
                      zIndex: 2,
                    }
                  : {}),
              }}
            >
              <button
                type="submit"
                disabled={!canSubmit}
                style={{
                  ...styles.btnPrimary,
                  ...(canSubmit ? {} : styles.btnDisabled),
                }}
              >
                {loading
                  ? translate('profile.withdraw.sending')
                  : translate('profile.withdraw.submit')}
              </button>

              {status && <div style={styles.statusText}>{status}</div>}
              {error && <div style={styles.errorText}>{error}</div>}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
